/* Base class selections, D&D 2024 / SRD 5.2.1 class feature tables.
 * Extra spells/cantrips granted by features require separate source records.
 * This module is CommonJS so Node and the client share the same authority.
 * Avoid object spread here: Babel would add an ES import, and Webpack would then lose every named export.
 */
const { divineOrderCantrips } = require('./divineOrder');
const { primalOrderCantrips } = require('./primalOrder');
const ordinary = Object.freeze([0,4,5,6,7,9,10,11,12,14,15,16,16,17,17,18,18,19,20,21,22]);
const half = Object.freeze([0,2,3,4,5,6,6,7,7,9,9,10,10,11,11,12,12,14,14,15,15]);
const PREPARED_SPELLS = Object.freeze({
  bard: ordinary, cleric: ordinary, druid: ordinary,
  sorcerer: Object.freeze([0,2,4,6,7,9,10,11,12,14,15,16,16,17,17,18,18,19,20,21,22]),
  wizard: Object.freeze([0,4,5,6,7,9,10,11,12,14,15,16,16,17,18,19,21,22,23,24,25]),
  paladin: half, ranger: half,
  warlock: Object.freeze([0,2,3,4,5,6,7,8,9,10,10,11,11,12,12,13,13,14,14,15,15]),
});
const BASE_CANTRIPS = Object.freeze({ bard: 2, cleric: 3, druid: 2, sorcerer: 4, warlock: 2, wizard: 3 });
const normalize = (id) => String(id || '').trim().toLowerCase();

/** `character` is optional: it adds what a saved choice grants, like the Thaumaturge's extra cantrip. */
function getClassSpellLimits(classId, rawLevel, character) {
  const id = normalize(classId);
  const level = Number(rawLevel);
  if (!PREPARED_SPELLS[id] || !Number.isInteger(level) || level < 1 || level > 20) {
    return { preparedSpells: 0, cantrips: 0, maxSpellLevel: 0 };
  }
  const base = BASE_CANTRIPS[id] || 0;
  return {
    preparedSpells: PREPARED_SPELLS[id][level],
    cantrips: base ? base + (level >= 4 ? 1 : 0) + (level >= 10 ? 1 : 0) + (id === 'cleric' ? divineOrderCantrips(character) : 0) + (id === 'druid' ? primalOrderCantrips(character) : 0) : 0,
    maxSpellLevel: ['paladin', 'ranger'].includes(id) ? Math.ceil(level / 4)
      : id === 'warlock' ? Math.min(5, Math.ceil(level / 2)) : Math.min(9, Math.ceil(level / 2)),
  };
}

// When each class can change what it has (2024): 'level', one when it gains a level; 'rest', its whole list after a Long
// Rest; 'rest-one', one after a Long Rest. Filling room up to the limit is always allowed; these limit replacing.
const SPELL_CHANGES = Object.freeze({
  bard: { spells: 'level', cantrips: 'level' }, cleric: { spells: 'rest', cantrips: 'level' }, druid: { spells: 'rest', cantrips: 'level' },
  paladin: { spells: 'rest-one' }, ranger: { spells: 'rest-one' }, sorcerer: { spells: 'level', cantrips: 'level' },
  warlock: { spells: 'level', cantrips: 'level' }, wizard: { spells: 'rest', cantrips: 'rest-one' },
});
const CHANGE_FIELD = Object.freeze({ spells: 'spellChanges', cantrips: 'cantripChanges' });
const KINDS = Object.freeze(['spells', 'cantrips']);

const classSpellIds = (spells, classId, kind) => (spells || [])
  .filter((spell) => spell?.source?.sourceType === 'class' && spell.source.sourceId === classId && (Number(spell.level) === 0) === (kind === 'cantrips'))
  .map((spell) => spell.spellId);
const recordIds = (records, kind) => records.filter((record) => (Number(record.level) === 0) === (kind === 'cantrips')).map((record) => record.spellId);
const baselineOf = (character, classId) => character?.spellcastingState?.preparedBaseline?.[classId] || null;
const slotsSpent = (character) => {
  const state = character?.spellcastingState;
  return Object.values(state?.spellcasting?.spentByLevel || {}).some((spent) => Number(spent) > 0) || Number(state?.pactMagic?.spent) > 0;
};

/**
 * The list each class changes from, with how many it can replace: taken after a Long Rest for the classes that change then.
 * Measuring from this list, not the last save, lets a player swap a spell back without using a change.
 */
function spellBaselineAfterRest(character) {
  const baseline = {};
  for (const entry of character?.occupation || []) {
    const classId = normalize(entry?.Occupation || entry?.Name);
    const rules = SPELL_CHANGES[classId];
    if (!rules) continue;
    const prior = baselineOf(character, classId) || {};
    const next = {};
    for (const kind of KINDS) {
      if (!rules[kind]) continue;
      const atLevel = rules[kind] === 'level';
      next[kind] = atLevel && prior[kind] ? prior[kind] : classSpellIds(character.spells, classId, kind);
      next[CHANGE_FIELD[kind]] = rules[kind] === 'rest' ? 'any' : rules[kind] === 'rest-one' ? 1 : prior[CHANGE_FIELD[kind]] ?? 0;
    }
    baseline[classId] = next;
  }
  return baseline;
}

/** The class's baseline once it gains a level: one replacement for what changes at a level. Null if nothing does. */
function spellBaselineAfterLevel(character, classId) {
  const rules = SPELL_CHANGES[normalize(classId)];
  if (!rules || !KINDS.some((kind) => rules[kind] === 'level')) return null;
  const id = normalize(classId);
  const next = Object.assign({}, baselineOf(character, id));
  for (const kind of KINDS) if (rules[kind] === 'level') {
    next[kind] = classSpellIds(character.spells, id, kind);
    next[CHANGE_FIELD[kind]] = 1;
  }
  return next;
}

/**
 * Replacements left for this class: a number, 'any' (a whole list, until a spell slot is spent after the rest), or null when
 * no limit applies yet: a character with no baseline hasn't had a Long Rest or level since these rules arrived.
 */
function spellChangesLeft(character, classId, kind) {
  const id = normalize(classId);
  const baseline = baselineOf(character, id);
  if (!SPELL_CHANGES[id]?.[kind] || !baseline?.[kind]) return null;
  const allowed = baseline[CHANGE_FIELD[kind]] ?? 0;
  if (allowed === 'any') return slotsSpent(character) ? 0 : 'any';
  const current = classSpellIds(character.spells, id, kind);
  return Math.max(0, allowed - baseline[kind].filter((spellId) => !current.includes(spellId)).length);
}

/** Why this selection can't be saved, or ''. Only removing something counts; a spell that returns gives its change back. */
function spellChangeProblem(character, classId, records, { inCombat = false } = {}) {
  const id = normalize(classId);
  for (const kind of KINDS) {
    const next = recordIds(records, kind);
    if (!classSpellIds(character?.spells, id, kind).some((spellId) => !next.includes(spellId))) continue;
    if (inCombat) return 'Spells can’t be changed during combat.';
    const baseline = baselineOf(character, id);
    if (!SPELL_CHANGES[id]?.[kind] || !baseline?.[kind]) continue;
    const allowed = baseline[CHANGE_FIELD[kind]] ?? 0;
    const open = allowed === 'any' ? !slotsSpent(character) : baseline[kind].filter((spellId) => !next.includes(spellId)).length <= allowed;
    if (open) continue;
    const name = id.charAt(0).toUpperCase() + id.slice(1), what = kind === 'cantrips' ? 'cantrip' : 'spell';
    const rule = SPELL_CHANGES[id][kind];
    if (rule === 'rest') return `${name} spells change after a Long Rest, before you spend a spell slot.`;
    return rule === 'rest-one' ? `A ${name} can replace one ${what} after a Long Rest.` : `A ${name} can replace one ${what} each time it gains a level.`;
  }
  return '';
}

module.exports = { PREPARED_SPELLS, SPELL_CHANGES, getClassSpellLimits, spellBaselineAfterRest, spellBaselineAfterLevel, spellChangesLeft, spellChangeProblem };
